'use client'

import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { resolveRouteSkeleton } from '@/components/skeletons/RouteLoading'
import { registerNavigationPendingHandler } from '@/lib/navigation-pending'
import { SITE_PAGE_BG } from '@/lib/site-layout'
import { cn } from '@/lib/utils'

const SHOW_DELAY_MS = 80
const SAFETY_MS = 8_000

function normalizePath(path: string): string {
  if (!path) return '/'
  const bare = path.split('?')[0]?.split('#')[0] || '/'
  if (bare.length > 1 && bare.endsWith('/')) return bare.slice(0, -1)
  return bare
}

function currentKey(pathname: string, search: string): string {
  const p = normalizePath(pathname)
  return search ? `${p}?${search}` : p
}

function pendingKeyFromRaw(raw: string): string | null {
  try {
    const url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://www.haber.com')
    if (typeof window !== 'undefined' && url.origin !== window.location.origin) return null
    const p = normalizePath(url.pathname)
    return url.search ? `${p}${url.search}` : p
  } catch {
    if (!raw.startsWith('/')) return null
    const bare = raw.split('#')[0] || '/'
    const [pathPart, qs] = bare.split('?')
    const p = normalizePath(pathPart || '/')
    return qs ? `${p}?${qs}` : p
  }
}

/**
 * Soft-nav skeleton mantığı — useSearchParams burada, kendi Suspense'i içinde.
 * ÖNEMLİ: Sayfa içeriği bu boundary'nin DIŞINDA kalır; aksi halde tüm sayfa
 * ayrı stream parçası olarak gelir ve hard reload'da "boş kabuk → içerik"
 * şeklinde iki aşamalı boyama (zıplama) görülür.
 */
function NavigationSkeletonController({
  onChange,
}: {
  onChange: (skeletonPath: string | null) => void
}) {
  const pathname = usePathname() || '/'
  const searchParams = useSearchParams()
  const search = searchParams?.toString() || ''
  const urlKey = currentKey(pathname, search)

  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const [skeletonPath, setSkeletonPath] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fromKeyRef = useRef<string | null>(null)

  const clearTimers = () => {
    if (showTimer.current) clearTimeout(showTimer.current)
    if (safetyTimer.current) clearTimeout(safetyTimer.current)
    showTimer.current = null
    safetyTimer.current = null
  }

  const clearPending = () => {
    clearTimers()
    fromKeyRef.current = null
    setPendingKey(null)
    setSkeletonPath(null)
    setVisible(false)
  }

  const startPending = (raw: string) => {
    const key = pendingKeyFromRaw(raw)
    if (!key || key === urlKey) return
    clearTimers()
    fromKeyRef.current = urlKey
    setPendingKey(key)
    setSkeletonPath(normalizePath(key.split('?')[0] || '/'))
    setVisible(false)
    showTimer.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    safetyTimer.current = setTimeout(clearPending, SAFETY_MS)
  }

  useEffect(() => {
    if (!pendingKey) return
    if (fromKeyRef.current !== null && urlKey !== fromKeyRef.current) {
      clearPending()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey, pendingKey])

  useEffect(() => {
    registerNavigationPendingHandler(startPending)
    return () => registerNavigationPendingHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey])

  useEffect(() => {
    const onPopState = () => clearPending()
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => () => clearTimers(), [])

  const showSkeleton = Boolean(pendingKey && visible && skeletonPath)

  useEffect(() => {
    onChange(showSkeleton ? skeletonPath : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSkeleton, skeletonPath])

  return null
}

/**
 * Soft-nav skeleton kabuğu.
 * ÖNEMLİ: document click capture ile skeleton açılmaz — Link soft-nav’ini
 * (özellikle manşet/swiper tıklamalarını) iptal edip URL’yi /’de bırakıyordu.
 * Skeleton yalnızca beginRouteTransition (programatik push) ile açılır.
 * Public Link geçişinde loading.tsx yok — eski sayfa yeni RSC gelene kadar kalır.
 */
export default function NavigationSkeletonGate({
  children,
}: {
  children: ReactNode
}) {
  const [skeletonPath, setSkeletonPath] = useState<string | null>(null)
  const showSkeleton = Boolean(skeletonPath)

  return (
    <div className="relative min-w-0">
      <div
        className={showSkeleton ? 'invisible pointer-events-none select-none' : undefined}
        aria-hidden={showSkeleton || undefined}
      >
        {children}
      </div>
      {showSkeleton && skeletonPath ? (
        <div
          className={cn(
            'absolute inset-0 z-[15] min-h-full overflow-y-auto',
            SITE_PAGE_BG,
          )}
          aria-live="polite"
          aria-busy="true"
        >
          {resolveRouteSkeleton(skeletonPath)}
        </div>
      ) : null}
      <Suspense fallback={null}>
        <NavigationSkeletonController onChange={setSkeletonPath} />
      </Suspense>
    </div>
  )
}
